import React, { useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useStatisticsQuery } from '../../hooks/useStatisticsQuery';
import { StatisticsTrend } from '../../types/supplierStatistics';
import { number } from './StatisticsTable';
export default function StatisticsTrends({
  params,
  refresh,
}: {
  params: Record<string, string | number>;
  refresh: number;
}) {
  const [bucket, setBucket] = useState('week'),
    [metric, setMetric] = useState('revenue');
  const { data, loading, error } = useStatisticsQuery<{
    series: StatisticsTrend[];
  }>('/trends', { ...params, bucket }, refresh);
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ flex: 1 }}>
          Utveckling över tid
        </Typography>
        <TextField
          select
          size="small"
          label="Intervall"
          value={bucket}
          onChange={(e) => setBucket(e.target.value)}
        >
          <MenuItem value="week">Vecka</MenuItem>
          <MenuItem value="month">Månad</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Mått"
          value={metric}
          onChange={(e) => setMetric(e.target.value)}
        >
          <MenuItem value="revenue">Omsättning (SEK)</MenuItem>
          <MenuItem value="units">Antal¹</MenuItem>
        </TextField>
      </Box>
      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : loading ? (
        <CircularProgress size={24} />
      ) : !data?.series?.length ? (
        <Typography color="text.secondary" sx={{ py: 4 }}>
          Inget trendunderlag för vald period.
        </Typography>
      ) : (
        <Box sx={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data?.series ?? []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" minTickGap={35} />
              <YAxis
                width={85}
                tick={{ fontSize: 12 }}
                tickFormatter={(v) => number(v)}
              />
              <Tooltip formatter={(v) => number(Number(v))} />
              <Legend />
              <Line
                name="Vald period"
                dataKey={metric}
                stroke="#1565c0"
                strokeWidth={2}
                dot={false}
              />
              <Line
                name="Samma datum föregående år (preliminärt)"
                dataKey={
                  metric === 'revenue' ? 'previousRevenue' : 'previousUnits'
                }
                stroke="#8e24aa"
                strokeDasharray="5 4"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
      <Typography variant="caption" color="text.secondary">
        Delveckor och delmånader begränsas till valda datum. Föregående år avser
        samma kalenderdatum, inte samma veckonummer. Noll betyder ingen
        observerad försäljning i underlaget; full täckning är inte verifierad.
      </Typography>
    </Paper>
  );
}
